import type { ExternalPlayer, PlayerNews, PlayerSeasonStats } from '../types';
import { formatDateTime } from '../lib/format';

function formatMarketValue(amount: number | null, currency: string | null): string {
  if (amount === null || !Number.isFinite(amount) || amount <= 0) return 'Non disponibile';
  const symbol = currency || '€';
  if (amount >= 1_000_000) {
    const millions = amount / 1_000_000;
    return `${symbol}${new Intl.NumberFormat('it-IT', { maximumFractionDigits: millions < 10 ? 1 : 0 }).format(millions)} mln`;
  }
  if (amount >= 1_000) {
    return `${symbol}${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(amount / 1_000)} mila`;
  }
  return `${symbol}${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(amount)}`;
}

function ageFromBirthDate(value: string | null): number | null {
  if (!value) return null;
  const birth = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getUTCFullYear() - birth.getUTCFullYear();
  const beforeBirthday = now.getUTCMonth() < birth.getUTCMonth()
    || (now.getUTCMonth() === birth.getUTCMonth() && now.getUTCDate() < birth.getUTCDate());
  if (beforeBirthday) age -= 1;
  return age;
}

export function PlayerInfo({ player, stats }: { player: ExternalPlayer; stats: PlayerSeasonStats | null }) {
  const age = ageFromBirthDate(player.birth_date);
  return (
    <section className="panel player-info-panel">
      <div className="panel-header compact">
        <div>
          <span className="eyebrow">Dati giocatore</span>
          <h2>{player.full_name}</h2>
        </div>
        {player.photo_url && <img className="player-photo" src={player.photo_url} alt="" loading="lazy" />}
      </div>

      <div className="player-profile-grid">
        <div><span>Squadra</span><strong>{player.current_team_name || stats?.team_name || 'Non disponibile'}</strong></div>
        <div><span>Ruolo</span><strong>{player.position || 'Non disponibile'}</strong></div>
        <div><span>Età</span><strong>{age !== null ? `${age} anni` : 'Non disponibile'}</strong></div>
        <div>
          <span>Valore di mercato</span>
          <strong>{formatMarketValue(player.market_value_amount, player.market_value_currency)}</strong>
          {player.market_value_recorded_at && <small>Rilevato il {new Intl.DateTimeFormat('it-IT').format(new Date(`${player.market_value_recorded_at}T00:00:00Z`))}</small>}
        </div>
      </div>

      {stats ? (
        <>
          <div className="player-season-title">
            <strong>Stagione {stats.season}/{String(stats.season + 1).slice(-2)}</strong>
            <span>{stats.competition_name || stats.team_name || ''}</span>
          </div>
          <div className="player-stat-grid">
            <div><strong>{stats.appearances ?? 0}</strong><span>Presenze</span></div>
            <div><strong>{stats.minutes ?? 0}</strong><span>Minuti</span></div>
            <div><strong>{stats.goals ?? 0}</strong><span>Gol</span></div>
            <div><strong>{stats.assists ?? 0}</strong><span>Assist</span></div>
          </div>
        </>
      ) : <p className="muted">Statistiche stagionali non ancora disponibili.</p>}

      <p className="muted player-data-note">Statistiche e valore di mercato sono dati di provider esterni. Il valore è indicativo e viene aggiornato solo mentre esiste almeno un’asta aperta per questo giocatore.</p>
    </section>
  );
}

export function PlayerNewsPanel({ news }: { news: PlayerNews[] }) {
  return (
    <section className="panel player-info-panel">
      <div className="panel-header compact">
        <div>
          <span className="eyebrow">Aggiornamenti</span>
          <h2>Ultime notizie</h2>
        </div>
      </div>

      <div className="player-news-list">
        {news.map((item) => (
          <a key={item.id} href={item.article_url} target="_blank" rel="noreferrer" className="player-news-item">
            <div><span className="news-category">{item.category || 'Notizia'}</span><strong>{item.title}</strong></div>
            <small>{item.source_name || 'Fonte esterna'}{item.published_at ? ` · ${formatDateTime(item.published_at)}` : ''}</small>
          </a>
        ))}
        {news.length === 0 && <p className="muted">Nessuna notizia recente trovata.</p>}
      </div>
    </section>
  );
}
